// src/modules/module_step3.tsx
import React, {
    useEffect,
    useState,
    useCallback,
    useRef
} from 'react';
import config from '../config/config';
import {
    Card,
    CardHeader,
    CardTitle,
    CardBody,
    CardSubtitle
} from '@progress/kendo-react-layout';
import { TextArea } from '@progress/kendo-react-inputs';
import styled from 'styled-components';
import { Button } from '@progress/kendo-react-buttons';
import axios from 'axios';
import { useForm } from '../contexts/FormContext';
import {
    Notification,
    NotificationGroup
} from '@progress/kendo-react-notification';
import GetUserAvatar from '../components/GetUserAvatar';

const StyledButton = styled(Button)`
    font-weight: bold;
    font-size: 20px;
`;

const ModuleStep3: React.FC = () => {
    const { formData } = useForm();
    const [notes, setNotes] = useState('');

    /* ---------- Refs ---------------------------------------------------- */
    const notesRef = useRef('');        // enthält stets den aktuellen Text
    const isDirty  = useRef(false);     // wurde seit dem Laden geändert?

    const [showNotification, setShowNotification] = useState(false);

    /* ---------- 1) Anmerkungen laden ----------------------------------- */
    useEffect(() => {
        if (!formData.jobID) return;
        axios
            .get(`${config.host}/jobs/${encodeURIComponent(formData.jobID)}`)
            .then(res => {
                const txt = res.data.Anmerkungen || '';
                setNotes(txt);
                notesRef.current = txt;
                isDirty.current  = false;
            })
            .catch(err =>
                console.error('❌ Fehler beim Laden der Anmerkungen:', err)
            );
    }, [formData.jobID]);

    /* ---------- 2) Speichern ------------------------------------------- */
    const saveNotes = useCallback(async () => {
        if (!formData.jobID || !isDirty.current) return false;

        try {
            await axios.put(
                `${config.host}/jobs/${encodeURIComponent(formData.jobID)}`,
                { Anmerkungen: notesRef.current }
            );
            isDirty.current = false;
            return true;
        } catch (err) {
            console.error('❌ Fehler beim Speichern der Anmerkungen:', err);
            return false;
        }
    }, [formData.jobID]);

    /* ---------- 3) Auto-Save bei Unmount / Refresh ---------------------- */
    useEffect(() => {
        const handleBeforeUnload = () => {
            saveNotes().catch(() => {});
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            // wird ausgeführt, wenn Komponente unmountet (Step-Wechsel)
            saveNotes().catch(() => {});
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [formData.jobID, saveNotes]);

    /* ---------- 4) Manuelles Speichern --------------------------------- */
    const handleManualSave = async () => {
        const ok = await saveNotes();
        if (ok) {
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 3000);
        }
    };

    /* ---------- 5) Render --------------------------------------------- */
    return (
        <div style={{ padding: 20 }}>
            {showNotification && (
                <NotificationGroup
                    style={{
                        position: 'fixed',
                        top: 30,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000
                    }}
                >
                    <Notification type={{ style: 'success', icon: true }}>
                        <span>Anmerkungen erfolgreich gespeichert.</span>
                    </Notification>
                </NotificationGroup>
            )}

            <Card orientation="vertical" className="custom-card">
                <CardHeader className="k-hbox custom-card-header">
                    <GetUserAvatar />
                    <div>
                        <CardTitle>Step&nbsp;3: Anmerkungen</CardTitle>
                        <CardSubtitle>
                            Fügen Sie hier Ihre Anmerkungen zum Projekt hinzu
                        </CardSubtitle>
                    </div>
                </CardHeader>

                <CardBody>
                    <div style={{ marginBottom: 20 }}>
                        <label>Anmerkungen</label>
                        <TextArea
                            placeholder="Ihre Anmerkungen hier eintragen..."
                            style={{ width: '100%', height: 150 }}
                            value={notes}
                            onChange={e => {
                                const val = e.value;
                                setNotes(val);
                                notesRef.current = val;
                                isDirty.current  = true;
                            }}
                        />
                    </div>

                    <StyledButton
                        themeColor="primary"
                        style={{ width: '100%' }}
                        onClick={handleManualSave}
                    >
                        Speichern
                    </StyledButton>
                </CardBody>
            </Card>
        </div>
    );
};

export default ModuleStep3;
